import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { App, Button, Checkbox, Form, Input, Modal, Skeleton, Typography } from "antd"
import { useState } from "react"
import { useNavigate, useParams } from "react-router"
import { useTheme } from "styled-components"
import { ContainerFull, LabeledBox, NameValueRow } from "../../components"
import { hashPassword, sha256, ShowMessagePage } from "../../config"
import { getUserDetails, setUserPasswordByAdmin, updateUserByAdmin } from "../../endpoints"

export function UserDetails(): JSX.Element {
    const navigate = useNavigate()
    const theme = useTheme()
    const queryClient = useQueryClient()
    const { modal } = App.useApp()

    const { userId } = useParams()

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getuserdetails", userId],
        queryFn: () => getUserDetails({ userId: parseInt(userId ?? "0") }),
        refetchOnMount: "always",
    })

    const updateMutation = useMutation({
        mutationFn: updateUserByAdmin,
        onSuccess: (data) => {
            if (data.code === 1000) {
                queryClient.invalidateQueries({ queryKey: ["getuserdetails", userId] })
                modal.success({ content: "User settings updated successfully" })
            } else {
                modal.error({ content: data.message })
            }
        },
        onError: (error) => {
            modal.error({ content: "Failed to update user settings" })
        },
    })

    const [passwordModalOpen, setPasswordModalOpen] = useState(false)
    const [passwordForm] = Form.useForm<{ password: string; password2: string }>()

    const passwordMutation = useMutation({
        // same hashing as reset/change password: bcrypt(sha256(password + "plab"))
        mutationFn: async (values: { password: string }) =>
            setUserPasswordByAdmin({
                userId: parseInt(userId ?? "0"),
                password: await hashPassword(await sha256(values.password + "plab")),
            }),
        onSuccess: (data) => {
            if (data.code === 1000) {
                setPasswordModalOpen(false)
                passwordForm.resetFields()
                modal.success({ content: data.message || "Password updated" })
            } else {
                modal.error({ content: data.message })
            }
        },
        onError: () => {
            modal.error({ content: "Failed to update password" })
        },
    })

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    if (data.code !== 1000) {
        return ShowMessagePage("Error", data.message)
    }
    const user = data.user
    if (!user) {
        return ShowMessagePage("Error", "User not found")
    }
    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Button
                type="link"
                key="backbutton"
                style={{ color: theme.primary }}
                onClick={() => {
                    navigate("/admin/users")
                }}
            >
                {"<< Back to all users"}
            </Button>
            <LabeledBox title={`  ${user.firstName} ${user.lastName}`}>
                <NameValueRow key="name" Name={"ID"} Value={user.id} />
                <NameValueRow
                    key="firstName"
                    Name={"First Name"}
                    Value={
                        <Typography.Text
                            editable={{
                                onChange: (value) => {
                                    const firstName = value.trim()
                                    if (!firstName || firstName === user.firstName) return
                                    updateMutation.mutate({ userId: user.id, firstName })
                                },
                            }}
                        >
                            {user.firstName}
                        </Typography.Text>
                    }
                />
                <NameValueRow
                    key="lastName"
                    Name={"Last Name"}
                    Value={
                        <Typography.Text
                            editable={{
                                onChange: (value) => {
                                    const lastName = value.trim()
                                    if (!lastName || lastName === user.lastName) return
                                    updateMutation.mutate({ userId: user.id, lastName })
                                },
                            }}
                        >
                            {user.lastName}
                        </Typography.Text>
                    }
                />
                <NameValueRow key="email" Name={"Email"} Value={user.email} />
                <NameValueRow key="institution" Name={"Institution"} Value={user.institution} />
                <NameValueRow key="phone" Name={"Phone"} Value={user.phone} />
                <NameValueRow key="cell" Name={"Cell"} Value={user.cell} />
                <NameValueRow key="userRole" Name={"Role"} Value={user.userRole} />
                <NameValueRow key="verified" Name={"Verified"} Value={user.verified ? "Yes" : "No"} />
                <NameValueRow key="active" Name={"Active"} Value={user.active ? "Yes" : "No"} />
                <NameValueRow key="ts" Name={"Date created"} Value={user.ts ? new Date(user.ts).toISOString() : ""} />
                <NameValueRow
                    key="lastLogin"
                    Name={"Last Login"}
                    Value={user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "Never"}
                />
                <NameValueRow key="lastLoginIp" Name={"Last Login IP"} Value={user.lastLoginIp ?? "N/A"} />
                <NameValueRow
                    key="password"
                    Name={"Password"}
                    Value={
                        <Button size="small" onClick={() => setPasswordModalOpen(true)}>
                            Set Password
                        </Button>
                    }
                />
                <Modal
                    title={`Set password for ${user.firstName} ${user.lastName}`}
                    open={passwordModalOpen}
                    okText="Set Password"
                    confirmLoading={passwordMutation.isPending}
                    onOk={() => passwordForm.submit()}
                    onCancel={() => {
                        setPasswordModalOpen(false)
                        passwordForm.resetFields()
                    }}
                >
                    <Form
                        form={passwordForm}
                        layout="vertical"
                        onFinish={(values) => passwordMutation.mutate({ password: values.password })}
                    >
                        <Form.Item
                            name="password"
                            label="New Password"
                            rules={[{ required: true, message: "Please enter a password" }]}
                        >
                            <Input.Password autoComplete="new-password" />
                        </Form.Item>
                        <Form.Item
                            name="password2"
                            label="Confirm Password"
                            dependencies={["password"]}
                            rules={[
                                { required: true, message: "Please confirm the password" },
                                ({ getFieldValue }) => ({
                                    validator: (_, value) =>
                                        !value || getFieldValue("password") === value
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("The passwords do not match")),
                                }),
                            ]}
                        >
                            <Input.Password autoComplete="new-password" />
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Group Settings Section */}
                <div style={{ 
                    marginTop: "2em", 
                    marginBottom: "1em", 
                    fontSize: "16px", 
                    fontWeight: "bold", 
                    color: theme.primary,
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: "1em"
                }}>
                    Group Settings
                </div>
                <NameValueRow key="groupName" Name={"Group"} Value={user.group_name || "No group"} />
                <NameValueRow 
                    key="receiveGroupOrderNotifications" 
                    Name={"Receive Group Order Notifications"} 
                    Value={
                        <Checkbox
                            checked={user.receive_group_order_notifications ?? false}
                            onChange={(e) => {
                                updateMutation.mutate({
                                    userId: user.id,
                                    receive_group_order_notifications: e.target.checked
                                })
                            }}
                        />
                    } 
                />
                <NameValueRow 
                    key="viewGroupOrders" 
                    Name={"View Group Orders"} 
                    Value={
                        <Checkbox
                            checked={user.view_group_orders ?? false}
                            onChange={(e) => {
                                updateMutation.mutate({
                                    userId: user.id,
                                    view_group_orders: e.target.checked
                                })
                            }}
                        />
                    }
                />
                <NameValueRow
                    key="viewGroupInvoices"
                    Name={"View Group Invoices"}
                    Value={
                        <Checkbox
                            checked={user.view_group_invoices ?? false}
                            onChange={(e) => {
                                updateMutation.mutate({
                                    userId: user.id,
                                    view_group_invoices: e.target.checked
                                })
                            }}
                        />
                    }
                />
                
                {/* Address Information Section */}
                <div style={{ 
                    marginTop: "2em", 
                    marginBottom: "1em", 
                    fontSize: "16px", 
                    fontWeight: "bold", 
                    color: theme.primary,
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: "1em"
                }}>
                    Address Information
                </div>
                <NameValueRow key="addressLine1" Name={"Address 1"} Value={user.address?.line1} />
                <NameValueRow key="addressLine2" Name={"Address 2"} Value={user.address?.line2} />
                <NameValueRow key="city" Name={"City"} Value={user.address?.city} />
                <NameValueRow key="state" Name={"State"} Value={user.address?.state} />
                <NameValueRow key="country" Name={"Country"} Value={user.address?.country} />
                <NameValueRow key="zipcode" Name={"Zipcode"} Value={user.address?.zip} />
                
                {/* Billing Information Section */}
                <div style={{ 
                    marginTop: "2em", 
                    marginBottom: "1em", 
                    fontSize: "16px", 
                    fontWeight: "bold", 
                    color: theme.primary,
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: "1em"
                }}>
                    Billing Information
                </div>
                <NameValueRow key="billingContactName" Name={"Billing Contact Name"} Value={user.billingAddress?.billingContactName || "Not specified"} />
                <NameValueRow key="billingEmail" Name={"Billing Email"} Value={user.billingAddress?.billingEmail || "Not specified"} />
                <NameValueRow key="billingAddressLine1" Name={"Billing Address 1"} Value={user.billingAddress?.line1 || "Not specified"} />
                <NameValueRow key="billingAddressLine2" Name={"Billing Address 2"} Value={user.billingAddress?.line2 || "Not specified"} />
                <NameValueRow key="billingCity" Name={"Billing City"} Value={user.billingAddress?.city || "Not specified"} />
                <NameValueRow key="billingState" Name={"Billing State"} Value={user.billingAddress?.state || "Not specified"} />
                <NameValueRow key="billingCountry" Name={"Billing Country"} Value={user.billingAddress?.country || "Not specified"} />
                <NameValueRow key="billingZipcode" Name={"Billing Zipcode"} Value={user.billingAddress?.zip || "Not specified"} />
            </LabeledBox>
        </ContainerFull>
    )
}
